import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from './cn';
import { InfoTip } from './InfoTip';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** A quieter second line or trailing text: a code, a plant, a count. */
  hint?: string;
  disabled?: boolean;
}

export interface SearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchSelectOption[];
  label?: string;
  /** Shown when nothing is chosen. */
  placeholder?: string;
  searchPlaceholder?: string;
  /** Offer × to go back to "" (the placeholder / "all" state). */
  clearable?: boolean;
  /**
   * The search box appears once the list is longer than this. A five-item
   * list is read faster than it is searched.
   */
  searchAfter?: number;
  disabled?: boolean;
  required?: boolean;
  emptyMessage?: string;
  hint?: ReactNode;
  info?: ReactNode;
  error?: string | null;
  className?: string;
  /** For a dropdown with no visible label (a filter bar): what a screen reader says. */
  'aria-label'?: string;
  /** Height of the closed control: 'md' 40px (forms), 'sm' 36px (filter rows). */
  size?: 'sm' | 'md';
}

/**
 * A dropdown with search, for a list the page already has (owner, 29 Oct
 * 2026: "one proper search-with-dropdown component, used everywhere").
 *
 * The same look and keyboard as `EntityCombobox`, which stays the picker for
 * lists that live on the server and page as you type. This one filters what
 * it was given: companies, plants, packages, partners, people.
 *
 * Matching is on every word typed, in the label or the hint, ignoring case —
 * "pune 01" finds "Pune Manufacturing Unit · PUNE-01".
 */
export function SearchSelect({
  value, onChange, options, label, placeholder = 'Select…', searchPlaceholder = 'Search…',
  clearable = false, searchAfter = 7, disabled, required, emptyMessage, hint, info, error, className,
  size = 'md', ...rest
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const labelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // The list floats on the page (a portal), so a dialog or a scrolling card
  // can never clip it. Placed under the field, or above it when there is no
  // room below, and kept there while anything scrolls.
  const [place, setPlace] = useState<CSSProperties | null>(null);
  useLayoutEffect(() => {
    if (!open) { setPlace(null); return undefined; }
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const room = window.innerHeight - r.bottom;
      const above = room < 320 && r.top > room;
      setPlace({
        position: 'fixed', left: r.left, width: Math.max(r.width, 240), zIndex: 60,
        ...(above ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open]);
  // Outside the field and outside the floating list closes it; Escape closes
  // it without also closing the dialog it sits in.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !popRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [open]);

  const selected = options.find((o) => o.value === value) ?? null;
  const searchable = options.length > searchAfter;

  const shown = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return options;
    return options.filter((o) => {
      const text = `${o.label} ${o.hint ?? ''}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [options, query]);

  // Opening starts on the current choice, with a fresh search.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const at = options.findIndex((o) => o.value === value);
    setActive(at >= 0 ? at : 0);
    if (searchable) setTimeout(() => inputRef.current?.focus(), 0);
    else setTimeout(() => listRef.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => { setActive(0); }, [query]);
  // Keep the keyboard row in view.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function choose(o: SearchSelectOption) {
    if (o.disabled) return;
    onChange(o.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onListKey(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, shown.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Home') { e.preventDefault(); setActive(0); return; }
    if (e.key === 'End') { e.preventDefault(); setActive(Math.max(shown.length - 1, 0)); return; }
    if (e.key === 'Tab') { setOpen(false); return; }
    if (e.key === 'Enter' && shown[active]) { e.preventDefault(); choose(shown[active]!); }
  }

  function onTriggerKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
    if ((e.key === 'Backspace' || e.key === 'Delete') && value && clearable && !disabled) { e.preventDefault(); onChange(''); }
  }

  return (
    <div className={cn('relative', className)} ref={rootRef}>
      {label && (
        <span className="mb-1.5 flex items-center gap-1">
          <span id={labelId} className="text-caption text-neutral-700">
            {label}
            {required && <span aria-hidden className="ml-0.5 text-danger-600">*</span>}
          </span>
          {info && <InfoTip label={`About ${label}`}>{info}</InfoTip>}
        </span>
      )}

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : rest['aria-label']}
        className={cn(
          'flex w-full items-center gap-2 rounded-input border bg-white px-3 text-left text-body',
          size === 'sm' ? 'h-9' : 'h-10',
          'transition-colors duration-hover focus:outline-none focus:ring-4 focus:ring-primary-500/15',
          error ? 'border-danger-600' : 'border-neutral-300 hover:border-neutral-400 focus:border-primary-500',
          disabled && 'cursor-not-allowed bg-neutral-50 text-neutral-400',
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-neutral-500')}>
          {selected ? selected.label : placeholder}
          {selected?.hint && <span className="ml-1.5 text-neutral-500">· {selected.hint}</span>}
        </span>
        {selected && value && clearable && !disabled && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear selection"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onChange(''); } }}
            className="inline-flex h-6 w-6 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
          >
            <X aria-hidden className="h-3.5 w-3.5" />
          </span>
        )}
        <ChevronDown aria-hidden className={cn('h-4 w-4 shrink-0 text-neutral-400 transition-transform', open && 'rotate-180')} />
      </button>

      {hint && !error && <span className="mt-1.5 block text-caption font-normal text-neutral-500">{hint}</span>}
      {error && <span className="mt-1.5 block text-caption text-danger-600">{error}</span>}

      {open && place && createPortal(
        <div ref={popRef} style={place} className="overflow-hidden rounded-card border border-neutral-200 bg-white shadow-dropdown animate-fade-in">
          {searchable && (
            <div className="relative border-b border-neutral-100">
              <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
              <input
                ref={inputRef}
                role="combobox"
                aria-expanded
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={shown[active] ? `${listId}-${active}` : undefined}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onListKey}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="h-10 w-full border-0 bg-transparent pl-9 pr-8 text-body text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
              />
              {query && (
                <button type="button" aria-label="Clear search"
                  onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                  className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700">
                  <X aria-hidden className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={searchable ? -1 : 0}
            onKeyDown={searchable ? undefined : onListKey}
            aria-labelledby={label ? labelId : undefined}
            aria-activedescendant={!searchable && shown[active] ? `${listId}-${active}` : undefined}
            className="max-h-72 overflow-y-auto py-1 focus:outline-none"
          >
            {shown.length === 0 && (
              <li className="px-3 py-6 text-center text-small text-neutral-500">
                {query ? `Nothing matches “${query.trim()}”.` : (emptyMessage ?? 'Nothing to choose from.')}
              </li>
            )}
            {shown.map((o, i) => {
              const isSel = o.value === value;
              return (
                <li key={o.value || '__empty'} id={`${listId}-${i}`} data-index={i} role="option" aria-selected={isSel} aria-disabled={o.disabled || undefined}>
                  <button
                    type="button"
                    tabIndex={-1}
                    disabled={o.disabled}
                    onClick={() => choose(o)}
                    onMouseEnter={() => setActive(i)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-2 text-left text-small [@media(pointer:coarse)]:min-h-11',
                      o.disabled ? 'cursor-not-allowed text-neutral-400' : i === active ? 'bg-primary-50' : 'hover:bg-neutral-50',
                    )}
                  >
                    <Check aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', isSel ? 'text-primary-600' : 'text-transparent')} />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block', isSel ? 'font-medium text-neutral-900' : 'text-neutral-800')}>{o.label}</span>
                      {o.hint && <span className="block text-caption font-normal text-neutral-500">{o.hint}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {searchable && (
            <div className="border-t border-neutral-100 px-3 py-1.5 text-center text-caption font-normal text-neutral-500">
              {query ? `${shown.length} of ${options.length}` : `${options.length} in total`}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
